import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { AddressData, BullhornFieldType, Entities, IAddressFields, IOperator } from '../../data-rules-model';
import { DataRulesService } from '../../data-rules.service';
import { AutocompleteComponent } from '../../lib/autocomplete/autocomplete.component';
import { ChipListAutocompleteComponent, IChipItem } from '../../lib/chip-list-autocomplete/chip-list-autocomplete.component';
import { DynamicItemsProvider, DynamicOptionsProvider } from '../../lib/option-items-providers';
import { ProviderFactory } from '../../lib/provider-factory';
import { IFieldMeta, MetaDataService } from '../../meta-data.service';

@Component({
  selector: 'app-address-criteria',
  templateUrl: './address-criteria.component.html',
  imports: [
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    NgForOf,
    NgIf,
    MatTooltip,
    NgClass,
    MatChipGrid,
    MatChipRow,
    MatIcon,
    MatChipInput,
    AutocompleteComponent,
    ChipListAutocompleteComponent,
    MatChipRemove
  ],
  styleUrls: ['./address-criteria.component.scss']
})

export class AddressCriteriaComponent implements OnInit, OnDestroy {

  @ViewChild('countryField')
  set fieldValue(v: AutocompleteComponent) {
    setTimeout(() => this.setCountryPickerComponent(v), 0);
  }

  @ViewChild('chipCountryField')
  set chipFieldValue(v: ChipListAutocompleteComponent) {
    setTimeout(() => this.setChipCountryPickerComponent(v), 0);
  }

  get operatorSelected(): string {
    return this.formGroup.get('operator').value;
  }

  @Input() formGroup: FormGroup;
  @Input() addressData; // TODO: Pull address meta in and remove those that aren't required
  @Input() operators: IOperator[];
  @Input() valuesReady ? = false;
  @Input() primaryEntity: Entities;

  addressForm: FormGroup;
  // Form Controls
  address1Control: FormControl;
  address2Control: FormControl;
  cityControl: FormControl;
  stateControl: FormControl;
  zipControl: FormControl;
  countryControl: FormControl;
  addressFields: IAddressFields[];
  noFieldsEnabled = false;
  allFieldsEnabled = false;
  countryAddressField: IAddressFields;
  fieldSelected: FormControl;
  infoMessage: string;
  private chipPickerComponent: ChipListAutocompleteComponent;
  private fieldPickerComponent: AutocompleteComponent;
  protected readonly DataRulesService = DataRulesService;

  constructor(
      private formBuilder: FormBuilder,
      private providerFactory: ProviderFactory,
      protected metaDataService: MetaDataService,
      protected dataRulesService: DataRulesService
  ) {
  }

  ngOnInit() {
    this.removeAllControls();
    this.setUpFormControls();
  }

  filterAddresses(enabled: boolean): IAddressFields[] {
    return this.addressFields.filter(field => field.enabled === enabled);
  }

  operatorChange(operator: MatSelectChange): void {
    this.addressFields.forEach((field) => {
      switch (operator.value) {
        case 'in':
        case 'notIn':
          if (!field.control.value) {
            field.control.setValue([]);
          } else if (!Array.isArray(field.control.value) && field.control.value) {
            field.control.setValue([field.control.value]);
          }
          break;
        case 'equals':
        case 'notEquals':
          if (Array.isArray(field.control.value) && field.control.value.length >= 1) {
            field.control.setValue(field.control.value[0]);
          } else if (field.control.value?.length < 1) {
            field.control.setValue('');
          }
          break;
      }
    });
    this.updateValidators();
  }

  addNewAddressField(selection: MatSelectChange): void {
    selection.source.value.enabled = true;
    this.addressForm.addControl(selection.source.value.controlName, selection.source.value.control);
    this.areFieldsIncluded();
    selection.source.value.control.setValue('');
    if (selection.value.name === 'Country') {
      this.initCountryPicker();
    }
    this.updateValidators();
  }

  removeAddressField(selection: IAddressFields): void {
    selection.enabled = false;
    this.addressForm.removeControl(selection.controlName);
    this.areFieldsIncluded();
    this.clickEventChipComponent();
    this.fieldSelected.setValue('');
    this.updateValidators();
  }

  ngOnDestroy() {
    this.removeAllControls();
  }

  removeInValue(selection: IAddressFields, val: string): void {
    const values = selection.control.value;
    const index = values.indexOf(val);
    if (index >= 0) {
      values.splice(index, 1);
      selection.control.setValue(values);
      selection.control.markAsDirty();
    }
  }

  addInValue(selection: IAddressFields, event: MatChipInputEvent): void {
    if (event.value) {
      selection.control.setValue([...selection.control.value, event.value]);
      event.input.value = '';
      selection.control.markAsDirty();
    }
  }

  clickEventChipComponent(): void {
    this.addressForm.markAsDirty();
    this.formGroup.setErrors(null);
  }

  private removeAllControls(): void {
    this.formGroup.removeControl('address1');
    this.formGroup.removeControl('address2');
    this.formGroup.removeControl('city');
    this.formGroup.removeControl('state');
    this.formGroup.removeControl('zip');
    this.formGroup.removeControl('countryID');
    this.formGroup.removeControl('value');
  }

  private setUpFormControls(): void {
    this.addressForm = this.formBuilder.group([]);
    this.addressForm.setValidators(this.hasControlsValidator);
    const addressValue = this.addressData ? this.addressData : new AddressData();
    this.address1Control = new FormControl(addressValue.address1, Validators.required);
    this.address2Control = new FormControl(addressValue.address2, Validators.required);
    this.cityControl = new FormControl(addressValue.city, Validators.required);
    this.stateControl = new FormControl(addressValue.state, Validators.required);
    this.zipControl = new FormControl(addressValue.zip, Validators.required);
    this.countryControl = new FormControl(addressValue.countryID, Validators.required);
    this.fieldSelected = new FormControl();
    this.countryAddressField = {
      name: 'Country', controlName: 'countryID', control: this.countryControl,
      enabled: addressValue.countryID !== undefined
    };
    this.addressFields = [
      { name: 'Address Line 1', controlName: 'address1', control: this.address1Control, enabled: addressValue.address1 !== undefined },
      { name: 'Address Line 2', controlName: 'address2', control: this.address2Control, enabled: addressValue.address2 !== undefined },
      { name: 'City', controlName: 'city', control: this.cityControl, enabled: addressValue.city !== undefined },
      { name: 'State', controlName: 'state', control: this.stateControl, enabled: addressValue.state !== undefined },
      { name: 'Zip', controlName: 'zip', control: this.zipControl, enabled: addressValue.zip !== undefined },
      this.countryAddressField
    ];

    this.populateAddressForm(addressValue);
    this.formGroup.addControl('value', this.addressForm);
    this.areFieldsIncluded();

    this.updateValidators();

    this.addressForm.valueChanges.subscribe((_) => {
      this.addressForm.markAsDirty();
    });
  }

  private hasControlsValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control as FormGroup;
    return Object.keys(formGroup.controls).length === 0 ? { noControls: true } : null;
  }

  private updateValidators() {
    const operator = this.formGroup.get('operator').value;
    switch (operator) {
      case 'isNull':
      case 'isNotNull':
        this.clearAllValidators();
        break;
      default:
        this.setRequiredValidators();
        break;
    }
  }

  private clearAllValidators(): void {
    this.addressFields.forEach(addressField => {
      addressField.control.clearValidators();
      addressField.control.updateValueAndValidity();
    });
  }

  private setRequiredValidators(): void {
    this.addressFields.forEach(addressField => {
      addressField.control.setValidators([Validators.required]);
      addressField.control.updateValueAndValidity();
    });
  }

  private populateAddressForm(addressValue: AddressData): void {
    this.addressFields.forEach((field) => {
      Object.keys(addressValue).forEach((controlName) => {
        if (field.enabled === true && field.controlName === controlName)
          this.addressForm.addControl(controlName, field.control);
      });
    });
  }

  private setChipCountryPickerComponent(acComponent: ChipListAutocompleteComponent): void {
    this.chipPickerComponent = acComponent;
    // fieldName will be null if this is a new action
    const fieldName = this.countryControl.value;
    if (this.chipPickerComponent && fieldName !== undefined) {
      const countryMeta: IFieldMeta = {
        // todo implement on secondary actions provide
        entityType: 'TBD' as Entities,
        fieldName: '',
        metaType: BullhornFieldType.TO_ONE,
        label: '',
        maxLength: 0,
        toManyType: null,
        toOneType: 'Country'
      };
      this.countryControl.disable();
      if (this.chipPickerComponent) {
        this.chipPickerComponent.loading();
        this.providerFactory.getItemsProvider(this.countryControl.value, countryMeta).subscribe(([provider, selItems]) => {
          this.chipPickerComponent.setItemsProvider(provider, selItems);
          this.chipPickerComponent.loadComplete();
          this.countryControl.enable();
        });
      }
    }
  }

  private setCountryPickerComponent(acComponent: AutocompleteComponent): void {
    this.fieldPickerComponent = acComponent;

    // fieldName will be null if this is a new action
    const fieldName = this.countryControl.value;
    if (this.fieldPickerComponent && fieldName !== undefined) {
      this.fieldPickerComponent.placeholder = 'Choose country...';
      // TODO: Move to Data rules model
      const countryMeta: IFieldMeta = {
        // todo implement on secondary actions provide
        entityType: 'TBD' as Entities,
        fieldName: '',
        metaType: 'COUNTRY',
        label: '',
        maxLength: 0,
        toManyType: null,
        toOneType: null
      };
      this.countryControl.disable();
      this.providerFactory.getOptionsProvider(this.countryControl.value, countryMeta).subscribe(provider => {
        this.fieldPickerComponent.setOptionsProvider(provider, this.countryControl.value);
        this.countryControl.enable();
      });
    }
  }

  private initCountryPicker(countryID?: number): void {
    // TODO: Move to Data Rules model
    const countryMeta: IFieldMeta = {
      // todo implement on secondary actions provide
      entityType: 'TBD' as Entities,
      fieldName: '',
      metaType: 'COUNTRY',
      label: '',
      maxLength: 0,
      toManyType: null,
      toOneType: null
    };
    const optionsProvider = new DynamicOptionsProvider(this.metaDataService, countryMeta);
    const selItems: IChipItem[] = [];
    const optionsItemsProvider = new DynamicItemsProvider(this.metaDataService, countryMeta, selItems);

    if (this.fieldPickerComponent)
      this.fieldPickerComponent.setOptionsProvider(optionsProvider, countryID);
    if (this.chipPickerComponent)
      this.chipPickerComponent.setItemsProvider(optionsItemsProvider, selItems);
  }

  private areFieldsIncluded(): void {
    const numberEnabled = this.filterAddresses(true).length;
    this.noFieldsEnabled = numberEnabled === 0;
    this.allFieldsEnabled = numberEnabled === this.addressFields.length;
  }
}
